import { useState, useCallback, useRef } from 'react';

const SUDO_STORAGE_KEY = 'adminSudoToken';
const SUDO_EXPIRY_KEY = 'adminSudoExpiry';
const SUDO_TTL_MS = 5 * 60 * 1000; // 5 minutes

export default function useSudoMode() {
  const [sudoModalOpen, setSudoModalOpen] = useState(false);
  const pendingActionRef = useRef(null);

  const getSudoToken = useCallback(() => {
    const token = sessionStorage.getItem(SUDO_STORAGE_KEY);
    const expiry = sessionStorage.getItem(SUDO_EXPIRY_KEY);
    if (token && expiry && Date.now() < Number(expiry)) {
      return token;
    }
    // Expired or missing
    sessionStorage.removeItem(SUDO_STORAGE_KEY);
    sessionStorage.removeItem(SUDO_EXPIRY_KEY);
    return null;
  }, []);

  const requireSudo = useCallback((action) => {
    const existing = getSudoToken();
    if (existing) {
      // Token still valid, execute action immediately
      action(existing);
      return;
    }
    // Need re-authentication
    pendingActionRef.current = action;
    setSudoModalOpen(true);
  }, [getSudoToken]);

  const onSudoSuccess = useCallback((token) => {
    sessionStorage.setItem(SUDO_STORAGE_KEY, token);
    sessionStorage.setItem(SUDO_EXPIRY_KEY, String(Date.now() + SUDO_TTL_MS));
    setSudoModalOpen(false);

    // Execute pending action
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      action(token);
    }
  }, []);

  const onSudoCancel = useCallback(() => {
    pendingActionRef.current = null;
    setSudoModalOpen(false);
  }, []);

  const clearSudoToken = useCallback(() => {
    sessionStorage.removeItem(SUDO_STORAGE_KEY);
    sessionStorage.removeItem(SUDO_EXPIRY_KEY);
  }, []);

  return {
    sudoModalOpen,
    requireSudo,
    onSudoSuccess,
    onSudoCancel,
    getSudoToken,
    clearSudoToken,
  };
}
